import api from '@api';
import type { Metric } from '@enums';
import {
  buildChartDataFromInsightChart,
  type InsightSeriesSuccess
} from '@utils';
import { useEffect, useMemo, useState } from 'react';
import type { InsightChart } from 'types/schema';

export interface RowLoadingState {
  state: 'loading';
}

export interface RowErrorState {
  state: 'error';
  id: string;
  message: string;
}

export interface RowSuccessState {
  state: 'ok';
  id: string;
  insight: InsightChart;
  series: InsightSeriesSuccess;
}

export type RowState = RowLoadingState | RowErrorState | RowSuccessState;

export const useInsightsQuery = (
  insightIds: string[],
  metric: Metric
): { rows: RowState[]; stableInsightIds: string[] } => {
  const insightIdsKey = insightIds.join(',');
  const [rows, setRows] = useState<RowState[]>(() =>
    insightIds.map(() => ({ state: 'loading' as const }))
  );
  const stableInsightIds = useMemo(
    () => insightIdsKey.split(',').filter(Boolean),
    [insightIdsKey]
  );

  useEffect(() => {
    let cancelled = false;

    setRows(stableInsightIds.map(() => ({ state: 'loading' as const })));

    (async () => {
      const results = await api.sources.getChartDataMany(stableInsightIds);

      if (cancelled) {
        return;
      }

      const next: RowState[] = results.map((item): RowState => {
        if (item.status === 'rejected') {
          return {
            state: 'error',
            id: item.id,
            message: 'Failed to load chart.'
          };
        }

        const built = buildChartDataFromInsightChart(item.data, metric);

        if (built.ok === false) {
          return {
            state: 'error',
            id: item.id,
            message: built.message
          };
        }

        return {
          state: 'ok' as const,
          id: item.id,
          insight: item.data,
          series: built
        };
      });

      setRows(next);
    })();

    return () => {
      cancelled = true;
    };
  }, [stableInsightIds, metric]);

  return { rows, stableInsightIds };
};
